import { z } from 'zod';

export const DocumentSearchToolName = 'documentSearch';

/**
 * DocumentSearch tool definition
 */
export const documentSearchArgsSchema = z.object({
  searchQuery: z
    .string()
    .describe(
      'Self-contained question describing the information needed. This is also the question used to rerank all retrieved passages',
    ),
  queryVariants: z
    .array(z.string().trim().min(1).max(1000))
    .max(3)
    .nullish()
    .describe(
      'Optional additional search phrasings or focused aspects of the same question (at most 3). The original query is always searched too; all candidates are deduplicated and reranked together against searchQuery. Make variants meaningfully different: use synonyms, alternative keywords, formal terminology or common-language wording, and distinct relevant aspects. Prefer concise keyword phrases: one using likely formal/domain terms, another using different everyday vocabulary or a complementary aspect. Do not merely reorder the same words or add generic words such as information or rules. Preserve entities, numbers, negation, constraints and document scope; do not invent facts or broaden the question. Omit for simple lookups; use separate calls for independent questions',
    ),
  context: z
    .string()
    .nullish()
    .describe(
      'Optional short clarification for an ambiguous query. It influences semantic matching only and is not added to keyword search. Omit it when the query is already self-contained',
    ),
  searchType: z
    .enum(['keywords', 'semantic', 'balanced'])
    .nullish()
    .describe(
      'Retrieval bias: "keywords" for exact terms, names, codes, or quoted phrases; "semantic" for conceptual/how/why questions; "balanced" (default) otherwise',
    ),
  documentIds: z
    .array(z.string())
    .nullish()
    .describe(
      'Restrict search to these document IDs when the user provides an ID or asks about a document identified in earlier results. Use the exact provided ID or the documentId/<id> returned by a tool; never invent one. Also use this to retrieve more passages from a partially covered document. Preserve the requested document scope when rephrasing.',
    ),
});

export const documentSearchContentSchema = z.string();

export type DocumentSearchArgs = z.infer<typeof documentSearchArgsSchema>;
export type DocumentSearchContent = z.infer<typeof documentSearchContentSchema>;
